/**
 * TableObjectStepThree class handles the configuration and interaction of a Handsontable instance.
 * It manages various settings and behaviors of the table, including sanitization, rendering, and data handling.
 */
import Handsontable from 'handsontable';

class TableObjectStepThree {
    /**
     * Constructor for TableObjectStepThree class.
     * @param {Object} $timeout - AngularJS $timeout service.
     * @param {Object} $translate - AngularJS $translate service for internationalization.
     * @param {Object} $config - Configuration service.
     * @param {Object} $signals - Signal service for event handling.
     * @param {Object} BridgeRuntimeService - Service for runtime operations.
     * @param {Object} Sanitize - Sanitization service.
     */
    constructor($timeout, $translate, $config, $signals, BridgeRuntimeService, Sanitize) {
        this.$timeout = $timeout;
        this.$translate = $translate;
        this.$config = $config;
        this.$signals = $signals;
        this.BridgeRuntimeService = BridgeRuntimeService;
        this.Sanitize = Sanitize;

        this._richTextEnabled = true;
        this._richTextEnabledCandidate = false;
        this._lockMergeCell = false;
        this._lockTableSize = false;
        this._lockDataEntry = false;
        this._lockHeaderStyling = false;
        this._addExtraCellRowHeader = true;
        this._htmlRendererSanitizeInstance = null;
        this._textRendererSanitizeInstance = null;
        this._pasteSanitizeInstance = null;
        this._autoFillSanitizeInstance = null;
        this._canSave = false;
        this._tableData = null;
        this._hotConfig = null;
        this._hotInstance = null;
        this.pdfFixesEnabled = true;
    }

    /**
     * Links the directive to the scope, element, attributes, and controller.
     * Initializes the table data and Handsontable configuration.
     * @param {Object} scope - AngularJS scope.
     * @param {Object} element - jQuery element.
     * @param {Object} attributes - Attributes of the element.
     * @param {Object} controller - Controller instance.
     */
    link(scope, element, attributes, controller) {
        this.scope = scope;
        this.element = element;
        this.controller = controller;

        this._richTextEnabledCandidate = scope.contentObject.getPreferences() && scope.contentObject.getPreferences().richTextEnabledCandidate;
        this._lockMergeCell = scope.contentObject.getPreferences() && scope.contentObject.getPreferences().lockMergeCell;
        this._lockTableSize = scope.contentObject.getPreferences() && scope.contentObject.getPreferences().lockTableSize;
        this._lockDataEntry = scope.contentObject.getPreferences() && scope.contentObject.getPreferences().lockDataEntry;
        this._lockHeaderStyling = scope.contentObject.getPreferences() && scope.contentObject.getPreferences().lockHeaderStyling;

        this.setupEventListeners();

        this.initTableData();
        this.initHotConfig();

        if (this._hotConfig) {
            this._hotInstance = new Handsontable(element.find('.handsontable')[0], this._hotConfig);
            scope.hotGuid = this._hotInstance.guid;
            this._hotInstance.updateTableHeight = this.updateTableHeight.bind(this);

            if (!isCMS || this.$config.isPreviewMode()) {
                this._hotInstance.undoRedo.clear();
            }
        } else {
            console.error("Not istantiating");
        }

        // Wrapper is the natural tab-stop for the table
        const wrapperEl = element.find('.handsontable-wrapper')[0];
        const onWrapperFocus = (event) => {
            if (!this._hotInstance) return;
            // Only react when focus lands on the wrapper itself, not when it
            // bubbles up from a child cell that just received focus.
            if (event.target !== wrapperEl) return;
            if (!this._hotInstance.getSelected()) {
                this._hotInstance.selectCell(0, 0);
            }
        };
        if (wrapperEl) wrapperEl.addEventListener('focus', onWrapperFocus, true);

        scope.$on('$destroy', () => {
            if (wrapperEl) wrapperEl.removeEventListener('focus', onWrapperFocus, true);
            if (this._hotInstance) {
                this._hotInstance.destroy();
                this._hotInstance = null;
            }
        });
    }

    /**
     * Sets up event listeners for table updates, rendering, and resets.
     */
    setupEventListeners() {
        this.scope.$on('tableObject_update', (event, value) => this.handleTableObjectUpdate(event, value));
        this.scope.$on('tableObject_render', () => this._hotInstance.render());
        this.scope.$on('tableObject_reset', () => this.handleTableObjectReset());
    }

    /**
     * Handles updates to the table object.
     * @param {Object} event - AngularJS event.
     * @param {Object} value - Update value.
     */
    handleTableObjectUpdate(event, value) {
        console.log("tableObject_update", event, value);
        if (!value || !this._hotInstance) return;
        this._canSave = false;

        if (value.hasOwnProperty('readOnly')) {
            this._hotConfig.readOnly = value.readOnly;
            if (this._hotConfig.readOnly) {
                this._hotConfig.undo = false;
            }
            this._lockDataEntry = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().lockDataEntry;
            this._lockTableSize = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().lockTableSize;
        } else if (value.hasOwnProperty('richTextEnabled')) {
            this._hotConfig.editor = value.richTextEnabled ? Handsontable.editors.RichTextEditor : Handsontable.editors.TextEditor;
            this._richTextEnabled = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().richTextEnabled;
        } else if (value.hasOwnProperty('action')) {
            this.handleTableAction(value.action);
        } else if (value !== 'updateData') {
            this._canSave = true;
            return;
        }

        this.initTableData();
        this.initHotConfig();

        if (Object.keys(this.getContextMenu().items).length) {
            this._hotConfig.contextMenu = this.getContextMenu();
        }

        this._hotInstance.updateSettings(this._hotConfig);
        this.setHotData(this._hotInstance);
    }

    /**
     * Handles specific table actions like adding or removing rows/columns.
     * @param {string} action - Action to perform.
     */
    handleTableAction(action) {
        switch (action) {
            case 'addRow':
                this._hotInstance.alter('insert_row', this._hotInstance.countRows(), 1);
                break;
            case 'removeRow':
                this._hotInstance.alter('remove_row', this._hotInstance.countRows() - 1, 1);
                break;
            case 'addCol':
                this._hotInstance.alter('insert_col', this._hotInstance.countCols(), 1);
                break;
            case 'removeCol':
                this._hotInstance.alter('remove_col', this._hotInstance.countCols() - 1, 1);
                break;
        }
    }

    /**
     * Handles resetting the table object to its initial state.
     */
    handleTableObjectReset() {
        this._canSave = false;
        this._richTextEnabled = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().richTextEnabled;
        this._lockMergeCell = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().lockMergeCell;
        this._lockDataEntry = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().lockDataEntry;
        this._lockTableSize = this.scope.contentObject.getPreferences() && this.scope.contentObject.getPreferences().lockTableSize;
        this._addExtraCellRowHeader = true;

        this.initTableData();
        this.initHotConfig();

        if (this._hotInstance) {
            this._hotInstance.clear();
            this._hotInstance.deselectCell();
        }

        if (this._hotInstance) {
            if (this._hotConfig) {
                this._hotInstance.destroy();
                this._hotInstance = new Handsontable(this.element.find('.handsontable')[0], this._hotConfig);
                this.scope.hotGuid = this._hotInstance.guid;
            }
        } else {
            this._hotInstance = new Handsontable(this.element.find('.handsontable')[0], this._hotConfig);
            this.scope.hotGuid = this._hotInstance.guid;
        }
    }

    /**
     * Zooms the table for PDF mode with a delay if necessary.
     */
    zoomTable() {
        if (!this.$config.isPDFMode()) {
            return;
        }
        if (this.pdfFixesEnabled) {
            console.log("Delaying zoomtable in PDF");
            if (this.zoomTablePromise) {
                this.$timeout.cancel(this.zoomTablePromise);
            }
            this.zoomTablePromise = this.$timeout(this.zoomTableReal, 1500, false);
        } else {
            this.zoomTableReal();
        }
    }

    /**
     * Performs the actual zooming of the table based on the width of the element.
     */
    zoomTableReal() {
        const he = this.element.find('.ht_master .htCore');
        const heWidth = he.width();
        const zoomFactor = this.element.width() / heWidth - 0.02;

        if (this.pdfFixesEnabled) {
            if (zoomFactor > 1) {
                console.log("Aborting zoom in PDF mode, preventing to zoom in: " + zoomFactor + " at " + this.element.width() + "/" + heWidth);
                return;
            }
            if (zoomFactor > 0.98 && zoomFactor < 1) {
                console.log("Aborting zoom in PDF mode, zoom factor is negligible: " + zoomFactor + " at " + this.element.width() + "/" + heWidth);
                return;
            }
            if (heWidth < 450) {
                console.log("Aborting zoom in PDF mode, used width just seems too low" + this.element.width() + "/" + heWidth);
                return;
            }
        }

        console.log("Zooming table with factor " + zoomFactor + " from width" + this.element.width() + "/" + heWidth);
        he.css('zoom', zoomFactor);
        he.css('width', this.element.width() + 'px !important');
    }

    /**
     * Initializes sanitization instances for different rendering contexts.
     */
    initSanitizeInstances() {
        this._htmlRendererSanitizeInstance = this.Sanitize({
            allowedTags: ['b', 'strong', 'i', 'u', 'span', 'em', 'p', 'input', 'select', 'option', 'img', 'br', 'sub', 'sup'],
            selfClosing: ['br'],
            allowedAttributes: {
                span: ['class', 'data-mathml'],
                img: ['class', 'src'],
                input: ['checked', 'name', 'required', 'type', 'value', 'autocapitalize', 'autocomplete', 'autocorrect', 'maxlength', 'spellcheck', 'style', 'size'],
                select: ['multiple', 'name', 'required', 'type', 'value', 'size'],
                option: ['value', 'selected']
            },
            allowedSchemesByTag: {
                img: ['data']
            }
        });
        this._textRendererSanitizeInstance = this.Sanitize({
            allowedTags: ['p', 'br']
        });

        if (this._richTextEnabled === true) {
            this._autoFillSanitizeInstance = this.Sanitize({
                allowedTags: ['b', 'strong', 'i', 'u', 'span', 'em', 'p', 'img', 'br', 'sub', 'sup'],
                selfClosing: ['br'],
                allowedAttributes: {
                    span: ['class', 'data-mathml'],
                    img: ['class', 'src']
                },
                allowedSchemesByTag: {
                    img: ['data']
                }
            });

            this._pasteSanitizeInstance = this.Sanitize({
                allowedTags: ['b', 'strong', 'i', 'u', 'span', 'em', 'p', 'img', 'br', 'sub', 'sup'],
                allowedAttributes: {
                    span: ['class', 'data-mathml'],
                    img: ['class', 'src']
                },
                allowedSchemesByTag: {
                    img: ['data']
                }
            });
        } else {
            this._pasteSanitizeInstance = this.Sanitize();
            this._pasteSanitizeInstance.disableAllTags();
            this._autoFillSanitizeInstance = this.Sanitize();
            this._autoFillSanitizeInstance.disableAllTags();
        }
    }

    /**
     * Initializes table data by setting up sanitization instances and fetching data from the controller.
     */
    initTableData() {
        this.initSanitizeInstances();
        this._tableData = this.controller.getStepData();
    }

    /**
     * Initializes the Handsontable configuration based on the table data and other settings.
     */
    initHotConfig() {
        this._hotConfig = null;

        if (!this._tableData) {
            this.initTableData();
        }

        if (!this._tableData[1] || !this._tableData[2]) {
            return;
        }

        let maxRows = this.controller.maxRows;
        let maxCols = this.controller.maxCols;

        if (this.scope.candidatePOV && this._lockTableSize) {
            maxRows = this._tableData[2].rows;
            maxCols = this._tableData[2].cols;
        }

        let numMergedCells = 0;

        var self = this;

        const config = {
            startRows: this._tableData[2].rows,
            startCols: this._tableData[2].cols,
            maxRows: maxRows,
            maxCols: maxCols,
            minRows: this.controller.minRows,
            minCols: this.controller.minCols,
            persistentState: true,
            rowHeaders: false,
            colHeaders: true,
            manualColumnResize: true,
            stretchH: 'all',
            autoRowSize: true,
            autoWrapRow: true,
            mergeCells: false,
            readOnly: this._lockDataEntry,
            candidatePOV: this.scope.candidatePOV,
            contentObject: this.scope.contentObject,
            langISO: this.scope.langISO,
            htmlRendererSanitizeInstance: this._htmlRendererSanitizeInstance,
            textRendererSanitizeInstance: this._textRendererSanitizeInstance,
            pasteSanitizeInstance: this._pasteSanitizeInstance,
            tableObjectController: this.controller,
            readOnlyCellClassName: 'htDimmed',
            contextMenu: null,
            renderAllRows: true,
            viewportColumnRenderingOffset: 100,
            afterGetColHeader: this.afterGetColHeader,
            cells: function () { return self.cellsCallback(this, ...arguments); },
            afterInit: this.afterInit.bind(this),
            init: function () { self._hotInstance = this; },
            afterRender: this.afterRender.bind(this),
            afterRenderer: this.afterRenderer.bind(this),
            afterChange: this.afterChange.bind(this),
            afterCreateCol: this.afterCreateCol.bind(this),
            afterRemoveCol: this.afterRemoveCol.bind(this),
            afterCreateRow: this.afterCreateRow.bind(this),
            afterRemoveRow: this.afterRemoveRow.bind(this),
            afterSelectionEnd: this.afterSelectionEnd.bind(this),
            modifyColHeader: this.modifyColHeader.bind(this),
            beforeChange: this.beforeChange.bind(this)
        };

        config.richTextEnabled = this.scope.candidatePOV ? !this._richTextEnabledCandidate : true;
        config.colHeaders = !this.scope.candidatePOV;
        config.editor = Handsontable.editors.RichTextEditor;

        if (!this._lockMergeCell) {
            config.mergeCells = this.controller.getMergeCells() ? this.controller.getMergeCells() : true;
            numMergedCells = this.controller.getMergeCells() ? this.controller.getMergeCells().length : 0;
        }

        if (this._lockDataEntry) {
            config.readOnly = true;
        }

        if (this.scope.candidatePOV) {
            config.readOnlyCellClassName = 'readonly-cell';
        }

        const contextMenuConfig = this.getContextMenu();
        if (Object.keys(contextMenuConfig.items).length) {
            config.contextMenu = contextMenuConfig;
        }

        if (this._addExtraCellRowHeader) {
            if (this.controller.getTableAlignment() === 'vertical') {
                config.startCols++;
                config.maxCols++;
                config.minCols++;
                config.fixedColumnsLeft = 1;
            } else {
                config.startRows++;
                config.maxRows++;
                config.minRows++;
            }
        }

        this._hotConfig = config;
    }

    /**
     * Modifies the column headers after they are rendered.
     * @param {number} col - The column index.
     * @param {HTMLElement} TH - The header cell element.
     */
    afterGetColHeader(col, TH) {
        const TR = TH.parentNode;
        function applyClass(elem, className) {
            if (!Handsontable.dom.hasClass(elem, className)) {
                Handsontable.dom.addClass(elem, className);
            }
        }

        applyClass(TH, 'ibBlue');
        if (TR) applyClass(TR, 'ibBlue');
    }

    /**
     * Callback for cell properties.
     * @param {Object} colSettings - The column settings.
     * @param {number} row - The row index.
     * @param {number} col - The column index.
     * @param {string} prop - The property identifier.
     * @returns {string} - The property value.
     */
    cellsCallback(colSettings, row, col, prop) {
        if (this._tableData[1]) {
            colSettings.renderer = this.htmlRenderer.bind(this);
        }
        return prop;
    }

    /**
     * Initializes data after Handsontable instance is initialized.
     */
    afterInit() {
        this.setHotData(this._hotInstance);
    }

    /**
     * Handles rendering after Handsontable instance is rendered.
     */
    afterRender() {
        if (this._hotInstance.mergeCells && this._numMergedCells !== this._hotInstance.mergeCells.mergedCellInfoCollection.length) {
            this._numMergedCells = this._hotInstance.mergeCells.mergedCellInfoCollection.length;
            const mergedCellsData = [];
            for (const key in this._hotInstance.mergeCells.mergedCellInfoCollection) {
                if (Number.isInteger(+key)) {
                    mergedCellsData.push(this._hotInstance.mergeCells.mergedCellInfoCollection[key]);
                }
            }
            this.controller.setMergeCells(mergedCellsData);
        }
    }

    /**
     * Custom renderer for cells.
     * @param {HTMLElement} td - The cell element.
     * @param {number} row - The row index.
     * @param {number} col - The column index.
     * @param {string} prop - The property identifier.
     * @param {string} value - The cell value.
     * @param {Object} cellProperties - The cell properties.
     */
    afterRenderer(td, row, col, prop, value, cellProperties) {
        if ($(td).find('input,select')) {
            $(td).find('input,select').off('change keyup');
            $(td).find('input,select').on('change keyup', (event) => {
                const element = $(event.target);
                let type, state;
                const name = element.attr('name');
                let key = name;
                const value = element.val();
                if (element.is('select')) {
                    type = 'select';
                } else if (element.attr('type') === 'radio') {
                    type = 'radio';
                } else if (element.attr('type') === 'text') {
                    type = 'text';
                } else if (element.attr('type') === 'checkbox') {
                    type = 'checkbox';
                    state = element.is(':checked');
                    key += value.replace(/[^a-zA-Z0-9_-]/g, "");
                }
                this.controller.setMCQData(key, name, value, type, state);
            });
            const mcqData = this.controller.getMCQData();
            if (mcqData) {
                for (const key in mcqData) {
                    if (mcqData.hasOwnProperty(key)) {
                        const elementValue = mcqData[key];
                        if (elementValue.type === 'select' || elementValue.type === 'text') {
                            $(td).find(`[name="${elementValue.name}"]`).val(elementValue.value);
                        } else if (elementValue.type === 'radio') {
                            $(td).find(`[name="${elementValue.name}"][value="${elementValue.value}"]`).prop("checked", true);
                        } else if (elementValue.type === 'checkbox') {
                            $(td).find(`[name="${elementValue.name}"][value="${elementValue.value}"]`).prop("checked", elementValue.state);
                        }
                    }
                }
            }
        }
    }

    /**
     * Handles changes in the table data.
     * @param {Array} change - The change array.
     * @param {string} source - The source of the change.
     */
    afterChange(change, source) {
        let changed = true;
        if (this._canSave) {
            if (change && source === 'edit') {
                changed = change.some(value => value[2] !== value[3]);
            }

            if (changed) {
                this.saveTableData();
            }
        }
        this.updateTableHeight();
    }

    /**
     * Handles column creation.
     * @param {number} index - The index of the new column.
     */
    afterCreateCol(index) {
        this.updateTableDataAfterCreateRowCol('col', index);
        this.updateRowsCells(this._hotInstance.countRows(), this._hotInstance.countCols());
        this.$timeout(() => this.updateTableHeight());
    }

    /**
     * Handles column removal.
     */
    afterRemoveCol() {
        this.updateRowsCells(this._hotInstance.countRows(), this._hotInstance.countCols());
        this.$timeout(() => this.updateTableHeight());
        this.saveTableData();
    }

    /**
     * Handles row creation.
     * @param {number} index - The index of the new row.
     */
    afterCreateRow(index) {
        this.updateTableDataAfterCreateRowCol('row', index);
        this.updateRowsCells(this._hotInstance.countRows(), this._hotInstance.countCols());
        this.$timeout(() => this.updateTableHeight());
    }

    /**
     * Handles row removal.
     */
    afterRemoveRow() {
        this.updateRowsCells(this._hotInstance.countRows(), this._hotInstance.countCols());
        this.$timeout(() => this.updateTableHeight());
        this.saveTableData();
    }

    /**
     * Handles the end of cell selection.
     * @param {number} startRow - The start row index.
     * @param {number} startColumn - The start column index.
     * @param {number} endRow - The end row index.
     * @param {number} endColumn - The end column index.
     */
    afterSelectionEnd(startRow, startColumn, endRow, endColumn) {
        this.element.find('.handsontableInput').each((key, textElement) => {
            const $textElement = $(textElement);
            if (!$textElement.attr('autocomplete') || !$textElement.attr('autocorrect') || !$textElement.attr('autocapitalize') || !$textElement.attr('spellcheck')) {
                $textElement.attr('autocomplete', 'off')
                    .attr('autocorrect', 'off')
                    .attr('autocapitalize', 'off')
                    .attr('spellcheck', false);
            }
        });
    }

    /**
     * Modifies the column header during its creation.
     * @param {number} column - The column index.
     */
    modifyColHeader(column) {
        const maxWidth = this.element.width();
        if (this._hotInstance !== null && !this.scope.candidatePOV) {
            this.columnLength++;
            if (this.columnLength === this._hotInstance.countCols()) {
                this.columnLength = 0;
                this.saveTableData();
            }
        }
    }

    /**
     * Handles changes before they are applied to the table data.
     * @param {Array} changes - The array of changes.
     * @param {string} source - The source of the changes.
     */
    beforeChange(changes, source) {
        for (const change of changes) {
            if (!change || !change[3]) continue;

            if (typeof change[3] === 'string' && change[3].match(/(<|&lt;)math.*MathML.*(>|&gt;).*(<|&lt;)\/math(>|&gt;)/)) {
                change[3] = '';
            }

            if (source === 'CopyPaste.paste') {
                change[3] = this._pasteSanitizeInstance.sanitizeHtml(change[3]);
            } else if (source === 'Autofill.fill') {
                change[3] = this._autoFillSanitizeInstance.sanitizeHtml(change[3]);
            }
        }
    }

    /**
     * Saves the table data.
     */
    saveTableData() {
        if (this._hotInstance) {
            this.controller.setTableData(this.getHotData());
        } else {
            console.error('Handsontable instance not found!');
        }
    }

    /**
     * Retrieves the data from the Handsontable instance.
     * @returns {Array} - The table data.
     */
    getHotData() {
        const cells = [];
        for (let row = 0; row <= this._hotInstance.countRows(); row++) {
            for (let col = 0; col <= this._hotInstance.countCols(); col++) {
                const cellMeta = this._hotInstance.getCellMeta(row, col);
                const cellData = this._hotInstance.getDataAtCell(row, col);
                const cellWidth = this._hotInstance.getColWidth(col);

                if (cellData || cellMeta.readOnly || cellWidth !== 50 || cellMeta.className) {
                    cells.push({
                        'row': row,
                        'col': col,
                        'data': cellData,
                        'readOnly': cellMeta.readOnly,
                        'width': cellWidth !== 50 ? cellWidth : null,
                        'className': cellMeta.className
                    });
                }
            }
        }

        return cells;
    }

    /**
     * Sets the data to the Handsontable instance.
     * @param {Object} handsontableInstance - The Handsontable instance.
     */
    setHotData(handsontableInstance) {
        //if (!handsontableInstance.table) return;

        if (this._tableData[3]) {
            const tableData = this._tableData[3].tableData;
            const plugin = handsontableInstance.getPlugin('manualColumnResize');

            if (Array.isArray(tableData)) {
                let needsRender = false;
                for (const value of tableData) {
                    const row = value.row;
                    const col = value.col;

                    if (value.width) {
                        plugin.setManualSize(col, value.width);
                        needsRender = true;
                    }

                    if (value.className) {
                        handsontableInstance.setCellMeta(row, col, 'className', value.className);
                        needsRender = true;
                    }

                    if (row <= this._tableData[2].rows && col <= this._tableData[2].cols) {
                        if (handsontableInstance.getCell(row, col)) {
                            if (value.data) {
                                handsontableInstance.setDataAtCell(row, col, value.data);
                            }

                            if (value.readOnly === true) {
                                handsontableInstance.setCellMeta(row, col, 'readOnly', true);
                                needsRender = true;
                            }
                        }
                    }
                }
                if (needsRender) {
                    handsontableInstance.render();
                }
            }
            if (this.$config.isPDFMode()) {
                this.zoomTable();
            }
        }
        if (handsontableInstance.undoRedo) {
            handsontableInstance.undoRedo.clear();
        }

        this._canSave = true;

        this.$timeout(() => {
            this.updateTableHeight();
            this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST_CLOSED, this.scope.contentObject.getUUID(), this.scope.contentObject.getObjectType() + "_step3");
        });
    }

    /**
     * Retrieves the context menu configuration.
     * @returns {Object} - The context menu configuration.
     */
    getContextMenu() {
        const ctxMenu = { items: {} };

        if (!this.scope.candidatePOV && !this._lockDataEntry) {
            ctxMenu.items['make_read_only'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.READ-ONLY"),
                callback: (key, coords) => {
                    const atLeastOneReadOnly = this.checkSelectionReadOnlyConsistency(this._hotInstance);

                    this._hotInstance.getSelectedRange().forAll((r, c) => {
                        this._hotInstance.getCellMeta(r, c).readOnly = !atLeastOneReadOnly;
                    });

                    this.saveTableData();
                    this._hotInstance.render();
                }
            };
        }

        if (!this.scope.candidatePOV || (this.scope.candidatePOV && !(this._lockTableSize || this._lockDataEntry))) {
            ctxMenu.items['remove_row'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.REMOVE-ROW")
            };
            ctxMenu.items['remove_col'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.REMOVE-COL")
            };
            ctxMenu.items['row_above'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.ROW-ABOVE")
            };
            ctxMenu.items['row_below'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.ROW-BELOW")
            };
            ctxMenu.items['col_left'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.COL-LEFT")
            };
            ctxMenu.items['col_right'] = {
                name: this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.COL-RIGHT")
            };
            if (!this._lockHeaderStyling || !this.scope.candidatePOV) {
                ctxMenu.items['set_header'] = {
                    name: () => this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.SET-AS-HEADER-CELL"),
                    callback: () => this.setCellAsHeader(),
                };
            }
            if (!this._lockHeaderStyling || !this.scope.candidatePOV) {
                ctxMenu.items['unset_header'] = {
                    name: () => this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.UNSET-AS-HEADER-CELL"),
                    callback: () => this.unsetCellAsHeader(),
                    disabled: () => {
                        const cellMeta = this._hotInstance.getCellsMeta();
                        return cellMeta.filter(e => e.className !== undefined).length <= 0;
                    },
                }
            }
        }
        if (!this.scope.candidatePOV || (!this._lockMergeCell && !this._lockDataEntry)) {
            ctxMenu.items['mergeCells'] = {
                disabled: () => {
                    let disabled = false;
                    const selectedRange = this._hotInstance.getSelectedRange();
                    if (selectedRange) {
                        const cells = selectedRange.getAll();
                        if (cells) {
                            if (cells.length === 1) {
                                return true;
                            }
                            for (const cell of cells) {
                                const metaData = this._hotInstance.getCellMeta(cell.row, cell.col);
                                if (metaData && metaData.readOnly) {
                                    disabled = true;
                                    break;
                                }
                            }
                        }
                    }
                    return disabled;
                },
                name: () => {
                    const selected = this._hotInstance.getSelected();
                    if (this._hotInstance.mergeCells) {
                        const info = this._hotInstance.mergeCells.mergedCellInfoCollection.getInfo(selected[0], selected[1]);
                        if (info) {
                            return this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.UNMERGE-CELLS");
                        }
                    }
                    return this.$translate.instant("CONTENTOBJECT.TABEL.CTX-MENU.MERGE-CELLS");
                }
            };
        }

        return ctxMenu;
    }

    updateTableHeight() {
        if (!this._hotInstance) {
            console.warn("updateTableHeight: table not defined!");
            if (!this.timedUpdateTableHeightPromise && this.pdfFixesEnabled && this.$config.isPDFMode()) {
                console.log("Retrying in a sec");
                this.timedUpdateTableHeightPromise = this.$timeout(() => {
                    this.updateTableHeight();
                    this.zoomTable();
                }, 1000, false);
            }
            return;
        }
        const holderSelector = `#${this._hotInstance.guid} .ht_master .wtHolder`;
        const holderElement = $(holderSelector)[0];
        const hiderSelector = `#${this._hotInstance.guid} .ht_master .wtHolder >.wtHider`;
        const hiderElement = $(hiderSelector)[0];

        if (holderElement) {
            const newHeight = hiderElement.scrollHeight + 20;
            if ($(holderElement).height() !== newHeight) {
                console.log("Setting height to " + newHeight);
                $(holderElement).height(newHeight);
            }
        }
    }

    updateRowsCells(rows, cols) {
        if (this.controller.getTableAlignment() === 'vertical') {
            cols--;
        } else {
            rows--;
        }

        this.controller.setStepData(2, {
            rows: rows,
            cols: cols
        });
    }

    updateTableDataAfterCreateRowCol(dir, index) {
        if (dir !== 'col' && dir !== 'row') return;
        const data = this._tableData[3];
        for (const key in data.tableData) {
            if (data.tableData.hasOwnProperty(key)) {
                if (data.tableData[key][dir] > (index - 1)) {
                    data.tableData[key][dir] += 1;
                }
            }
        }

        // Save table data
        this.controller.setTableData(data.tableData);
    }

    firstRowRenderer(instance, td, row, col, prop, value, cellProperties) {
        const cellMeta = instance.getCellMeta(row, col);

        if (cellMeta && cellMeta.readOnly) {
            Handsontable.renderers.TextRenderer.apply(this, arguments);
            td.innerHTML = Handsontable.helper.stringify(value);
        } else {
            Handsontable.renderers.TextRenderer.apply(this, arguments);
        }

        $(td).addClass('label-cell');
        return td;
    }

    setCellAsHeader() {
        const selected = [this._hotInstance.getSelected()] || [];

        for (const selection of selected) {
            const [row1, col1, row2, col2] = selection;
            const startRow = Math.max(Math.min(row1, row2), 0);
            const endRow = Math.max(row1, row2);
            const startCol = Math.max(Math.min(col1, col2), 0);
            const endCol = Math.max(col1, col2);
            for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
                for (let columnIndex = startCol; columnIndex <= endCol; columnIndex++) {
                    this._hotInstance.setCellMeta(rowIndex, columnIndex, 'className', 'label-cell');
                }
            }
        }

        this._hotInstance.render();
        this.saveTableData();
    }

    unsetCellAsHeader() {
        const selected = [this._hotInstance.getSelected()] || [];

        for (const selection of selected) {
            const [row1, col1, row2, col2] = selection;
            const startRow = Math.max(Math.min(row1, row2), 0);
            const endRow = Math.max(row1, row2);
            const startCol = Math.max(Math.min(col1, col2), 0);
            const endCol = Math.max(col1, col2);
            for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
                for (let columnIndex = startCol; columnIndex <= endCol; columnIndex++) {
                    this._hotInstance.setCellMeta(rowIndex, columnIndex, 'className', undefined);
                }
            }
        }

        this._hotInstance.render();
        this.saveTableData();
    }

    htmlRenderer(instance, td, row, col, prop, value, cellProperties) {
        Handsontable.renderers.TextRenderer.apply(this, arguments);

        if (!value) value = '';

        let escaped = Handsontable.helper.stringify(value);
        let mcqInside = false;
        if ($('<div>' + escaped + '</div>').find(':input,select').length > 0) {
            td.style.textAlign = 'left';
            $(td).addClass('mcq-content');
            mcqInside = true;
            if (!isCMS || this.$config.isPreviewMode()) {
                instance.setCellMeta(row, col, 'readOnly', true);
                instance.setCellMeta(row, col, 'disableVisualSelection', true);
            }
        }
        const cellMeta = instance.getCellMeta(row, col);
        if (this._richTextEnabled === true || cellMeta.readOnly || mcqInside) {
            escaped = this._htmlRendererSanitizeInstance.sanitizeHtml(escaped);
        } else {
            escaped = this._textRendererSanitizeInstance.sanitizeHtml(escaped);
        }
        td.innerHTML = escaped;

        if (this.scope.langISO === 'ur_UR') {
            $(td).addClass('urdu-font-force');
        }

        this.setColWidthForWiris(td, col);

        return td;
    }

    setColWidthForWiris(td, col) {
        const wirisImages = $(td).find('img.wiris-image-singleline');
        if (this.$config.isPDFMode() && this._richTextEnabled && wirisImages.length > 0) {
            let maxWidth = 0;
            wirisImages.each((index, value) => {
                const _imgSrc = $(value).attr('src');
                const img = new Image();
                img.onload = () => {
                    const colElement = $(this._hotInstance.table).find(`colgroup col:nth-child(${col + 1})`);

                    if (maxWidth < img.width) {
                        maxWidth = img.width + 10;
                        colElement.css({
                            'min-width': maxWidth
                        });
                        colElement.addClass('do-not-resize');
                        this.zoomTable();
                    }
                };
                img.src = _imgSrc;
            });
        }
    }

    checkSelectionReadOnlyConsistency(hot) {
        let atLeastOneReadOnly = false;

        hot.getSelectedRange().forAll((r, c) => {
            if (hot.getCellMeta(r, c).readOnly) {
                atLeastOneReadOnly = true;
                return false; // breaks forAll
            }
        });

        return atLeastOneReadOnly;
    }
}

export default function registerTableObjectStepThreeDirective(module) {
    module.directive('tableObjectStepThree', [
        '$timeout',
        '$translate',
        '$config',
        '$signals',
        'BridgeRuntimeService',
        'Sanitize',
        function ($timeout, $translate, $config, $signals, BridgeRuntimeService, Sanitize) {
            return {
                restrict: 'A',
                replace: true,
                require: '^asfTableObject',
                templateUrl: '/views/ib/contentobjects/table/step3.html',
                link: function (scope, element, attributes, controller) {
                    const tableObject = new TableObjectStepThree(
                        $timeout,
                        $translate,
                        $config,
                        $signals,
                        BridgeRuntimeService,
                        Sanitize
                    );
                    tableObject.link(scope, element, attributes, controller);
                }
            };
        }
    ]);
}